import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as api from '../../api';
import Input from '../Atoms/Input';
import Select from '../Atoms/Select';
import BlueButton from '../Atoms/BlueButton';
import Spinner from '../Atoms/Spinner';
import Section from '../Atoms/Section';
import { CountrySelectOptions } from '../Utils/Listings';

const PageContainer = styled.div`
    max-width: 700px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Logo = styled.img`
    width: 200px;
    display: block;
    margin: 0 auto 30px;
`;

const Title = styled.h1`
    font-size: 28px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 10px;
    color: #1a1a1a;
`;

const Subtitle = styled.p`
    font-size: 16px;
    text-align: center;
    color: #666;
    margin-bottom: 40px;
    line-height: 1.5;
`;

const WinnerBadge = styled.div`
    background: linear-gradient(135deg, #e3f2fd, #bbdefb);
    border: 1px solid #90caf9;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 30px;
    text-align: center;

    .project-name {
        font-size: 18px;
        font-weight: 700;
        color: #1565c0;
        margin-bottom: 5px;
    }

    .company-name {
        font-size: 14px;
        color: #555;
    }

    .editions {
        font-size: 12px;
        color: #888;
        margin-top: 8px;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 20px;

    label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin-bottom: 6px;
    }
`;

const ErrorMessage = styled.div`
    background: #fdecea;
    border: 1px solid #f5c6cb;
    border-radius: 8px;
    padding: 16px 20px;
    color: #721c24;
    text-align: center;
    margin: 40px 0;
    font-size: 15px;
`;

const SuccessMessage = styled.div`
    background: #d4edda;
    border: 1px solid #c3e6cb;
    border-radius: 8px;
    padding: 20px;
    color: #155724;
    text-align: center;
    margin: 40px 0;

    h3 {
        margin-bottom: 10px;
    }
`;

const AlreadyRegistered = styled.div`
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 20px;
    color: #856404;
    text-align: center;
    margin: 40px 0;

    a {
        color: #0d47a1;
        font-weight: 600;
    }
`;

const EDITION_LABELS = {
    'ec25d885-3dcf-455c-93b2-26041cb842e4': '1ère édition',
    '417b2020-721a-4c55-bd9f-d86a913f26ad': '2ème édition',
    'a3b7c9d1-e5f6-4a2b-8c3d-1e5f6a2b8c3d': '3ème édition'
};

const FD100SignupPage = (props) => {
    const { i18n } = useTranslation('common');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [alreadyRegistered, setAlreadyRegistered] = useState(false);
    const [winnerData, setWinnerData] = useState(null);

    useEffect(() => {
        if (!token) {
            setError(i18n.language === 'fr'
                ? "Lien d'invitation invalide. Veuillez utiliser le lien reçu par email."
                : "Invalid invitation link. Please use the link received by email.");
            setLoading(false);
            return;
        }

        api.ValidateFD100Token(token).then(res => {
            if (res.data.already_registered) {
                setAlreadyRegistered(true);
            } else {
                setWinnerData(res.data);
            }
            setLoading(false);
        }).catch(err => {
            setError(err.response?.data?.error || (i18n.language === 'fr' ? "Lien invalide ou expiré" : "Invalid or expired link"));
            setLoading(false);
        });
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        const form = e.target;
        const password = form.password.value;
        const passwordConfirm = form.passwordConfirm.value;

        if (password !== passwordConfirm) {
            setError(i18n.language === 'fr' ? "Les mots de passe ne correspondent pas" : "Passwords do not match");
            setSubmitting(false);
            return;
        }

        try {
            await api.RegisterFD100Winner({
                token,
                email: winnerData.email,
                password,
                firstname: form.firstname.value,
                lastname: form.lastname.value,
                jobtitle: form.jobtitle?.value || '',
                city: form.city?.value || '',
                country: form.country?.value || '',
                cat_gories_via: form.profile?.value || '',
                language: i18n.language
            });

            setSuccess(true);
            if (props.refreshUser) props.refreshUser();

            setTimeout(() => {
                navigate('/fd100-project-setup?token=' + token);
            }, 2000);

        } catch (err) {
            setError(err.response?.data?.error || (i18n.language === 'fr' ? "Erreur lors de l'inscription" : "Registration error"));
            setSubmitting(false);
        }
    };

    if (loading) {
        return <PageContainer><Spinner /></PageContainer>;
    }

    const editionsList = winnerData?.editions?.map(e =>
        EDITION_LABELS[e.event_id] || ''
    ).filter(Boolean).join(', ');

    return (
        <PageContainer>
            <Logo src="/img/LogosV3.png" alt="Le French Design" />

            <Title>
                {i18n.language === 'fr' ? 'Inscription Lauréat Le FD100' : 'Le FD100 Winner Registration'}
            </Title>
            <Subtitle>
                {i18n.language === 'fr'
                    ? "Félicitations ! Créez votre compte pour rejoindre la communauté Le FRENCH DESIGN et compléter votre profil lauréat."
                    : "Congratulations! Create your account to join the Le FRENCH DESIGN community and complete your winner profile."}
            </Subtitle>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {success && (
                <SuccessMessage>
                    <h3>{i18n.language === 'fr' ? 'Compte créé !' : 'Account created!'}</h3>
                    <p>{i18n.language === 'fr'
                        ? 'Redirection vers votre profil...'
                        : 'Redirecting to your profile...'}</p>
                </SuccessMessage>
            )}

            {alreadyRegistered && (
                <AlreadyRegistered>
                    <p>{i18n.language === 'fr'
                        ? "Vous avez déjà un compte sur la plateforme."
                        : "You already have an account on the platform."}</p>
                    <p style={{ marginTop: 10 }}>
                        <a href="/login">{i18n.language === 'fr' ? 'Se connecter' : 'Log in'}</a>
                    </p>
                </AlreadyRegistered>
            )}

            {winnerData && !success && !alreadyRegistered && (
                <>
                    <WinnerBadge>
                        <div className="project-name">{winnerData.project_name}</div>
                        <div className="company-name">{winnerData.company_name}</div>
                        {editionsList && (
                            <div className="editions">Le FD100 — {editionsList}</div>
                        )}
                    </WinnerBadge>

                    <form onSubmit={handleSubmit}>
                        <Section className="padding">
                            <FormGroup>
                                <label>Email</label>
                                <Input
                                    type="email"
                                    value={winnerData.email}
                                    disabled
                                    style={{ width: '100%', background: '#f5f5f5' }}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Prénom *' : 'First name *'}</label>
                                <Input
                                    name="firstname"
                                    required
                                    defaultValue={winnerData.first_name}
                                    style={{ width: '100%' }}
                                    placeholder={i18n.language === 'fr' ? 'Prénom' : 'First name'}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Nom *' : 'Last name *'}</label>
                                <Input
                                    name="lastname"
                                    required
                                    defaultValue={winnerData.last_name}
                                    style={{ width: '100%' }}
                                    placeholder={i18n.language === 'fr' ? 'Nom' : 'Last name'}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Poste actuel' : 'Current position'}</label>
                                <Input
                                    name="jobtitle"
                                    style={{ width: '100%' }}
                                    placeholder={i18n.language === 'fr' ? 'Poste actuel' : 'Current position'}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Ville' : 'City'}</label>
                                <Input
                                    name="city"
                                    style={{ width: '100%' }}
                                    placeholder={i18n.language === 'fr' ? 'Ville' : 'City'}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Pays' : 'Country'}</label>
                                <Select name="country" style={{ width: '100%' }} defaultValue="France">
                                    <CountrySelectOptions />
                                </Select>
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Votre profil' : 'Your profile'}</label>
                                <Select name="profile" style={{ width: '100%' }}>
                                    <option value="Créateur - Designer objet">{i18n.language === 'fr' ? 'Designer objet' : 'Object designer'}</option>
                                    <option value="Créateur - Architecte">{i18n.language === 'fr' ? 'Architecte' : 'Architect'}</option>
                                    <option value="Créateur - Designer espace">{i18n.language === 'fr' ? "Architecte d'intérieur" : 'Interior designer'}</option>
                                    <option value="Créateur - Graphiste">{i18n.language === 'fr' ? 'Graphiste' : 'Graphic designer'}</option>
                                    <option value="Créateur - Scénographe">{i18n.language === 'fr' ? 'Scénographe' : 'Set designer'}</option>
                                    <option value="Producteur - Editeur">{i18n.language === 'fr' ? 'Éditeur' : 'Editor'}</option>
                                    <option value="Producteur - Fabricant">{i18n.language === 'fr' ? 'Fabricant' : 'Manufacturer'}</option>
                                    <option value="Producteur - Distributeur">{i18n.language === 'fr' ? 'Distributeur' : 'Distributor'}</option>
                                    <option value="Producteur - Artisan d'art">{i18n.language === 'fr' ? "Artisan d'art" : 'Craftsman'}</option>
                                    <option value="Producteur - Autres">{i18n.language === 'fr' ? 'Autre' : 'Other'}</option>
                                </Select>
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Mot de passe *' : 'Password *'}</label>
                                <Input
                                    name="password"
                                    type="password"
                                    required
                                    minLength={6}
                                    style={{ width: '100%' }}
                                    placeholder={i18n.language === 'fr' ? 'Minimum 6 caractères' : 'Minimum 6 characters'}
                                />
                            </FormGroup>

                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Confirmer le mot de passe *' : 'Confirm password *'}</label>
                                <Input
                                    name="passwordConfirm"
                                    type="password"
                                    required
                                    minLength={6}
                                    style={{ width: '100%' }}
                                    placeholder={i18n.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                                />
                            </FormGroup>

                            <div style={{ marginTop: 10, marginBottom: 20 }}>
                                <Input type="checkbox" required />&nbsp;&nbsp;
                                <span style={{ fontSize: 13 }}>
                                    {i18n.language === 'fr' ? "J'ai lu et j'accepte " : "I have read and accept "}
                                    <a href='/documents/cvg-cgu.pdf' target='_blank' rel="noreferrer">
                                        {i18n.language === 'fr' ? "les conditions générales." : "the terms and conditions."}
                                    </a>
                                </span>
                            </div>

                            <BlueButton
                                className="centered"
                                style={{ width: '100%', opacity: submitting ? 0.6 : 1 }}
                                disabled={submitting}
                            >
                                {submitting
                                    ? (i18n.language === 'fr' ? 'Création en cours...' : 'Creating...')
                                    : (i18n.language === 'fr' ? 'Créer mon compte' : 'Create my account')
                                }
                            </BlueButton>
                        </Section>
                    </form>
                </>
            )}
        </PageContainer>
    );
};

export default FD100SignupPage;
